import { useCallback, useEffect, useState } from "react";
import { skipData } from "../utils/utils";
import useDebounce from "./useDebounce";

interface UseTableType<T> {
  defaultPage?: number;
  defaultLimit?: number;
  hideSkipLimit?: boolean;
  defaultFilter?: T;
  defaultKeyword?: string;
}

export default function useTable<T>(props?: UseTableType<T>) {
  const defaultPage = props?.defaultPage || 1,
    defaultLimit = props?.defaultPage || 10;

  const [page, setPage] = useState(defaultPage);
  const [limit, setLimit] = useState(defaultLimit);
  const [isResetPagination, setIsResetPagination] = useState(false);
  const {
    keyword,
    inputValue,
    handleInputChange: handleSearch,
    handleEnterValue,
  } = useDebounce(500, props?.defaultKeyword || "");
  const [filter, setFilter] = useState<T>(props?.defaultFilter || ({} as T));

  const resetPagination = useCallback(() => {
    setIsResetPagination(true);
  }, []);

  const handleLimit = (val: number) => {
    setLimit(Number(val));
    handlePagination(defaultPage, Number(val));
  };

  const handlePagination = useCallback(
    (paginationPage: number, paginationLimit: number) => {
      setPage(paginationPage);
      setLimit(paginationLimit);
    },
    []
  );

  // When filtering, redirect table in first page
  useEffect(() => {
    if (isResetPagination) {
      handlePagination(defaultPage, defaultLimit);
      setIsResetPagination(false);
    }
  }, [handlePagination, isResetPagination, defaultLimit, defaultPage]);

  // handle filter
  const handleFilter = <K extends keyof T>(
    filterType: K,
    value: T[K],
    hideResetPagination?: boolean
  ) => {
    setFilter((prev) =>
      prev
        ? ({
            ...prev,
            [filterType]: value,
          } as unknown as T)
        : ({
            [filterType]: value,
          } as unknown as T)
    );

    if (!hideResetPagination) resetPagination();
  };

  // reset pagination when search
  useEffect(() => {
    if (keyword) resetPagination();
  }, [keyword, resetPagination]);

  return {
    page,
    limit,
    handleLimit,
    handlePagination,
    resetPagination,
    keyword,
    inputValue,
    filter: {
      ...(!props?.hideSkipLimit ? { limit, skip: skipData(page, limit) } : {}),
      ...filter,
    } as T,
    handleSearch,
    handleFilter,
    handleSearchEnter: handleEnterValue,
  };
}
